import React, { useState, useEffect } from 'react';

function getTimeLeft(expiresAt) {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return { days, hours, minutes, seconds, totalMs: diff };
}

function TimeUnit({ value, label, tone, pad }) {
  const display = pad ? String(value).padStart(2, '0') : value;
  return (
    <div className="flex flex-col items-center">
      <span className={`font-display text-2xl sm:text-3xl font-bold tabular-nums leading-none ${tone}`}>
        {display}
      </span>
      <span className="text-[10px] text-gray-600 font-medium uppercase tracking-wider mt-1.5">
        {label}
      </span>
    </div>
  );
}

function Separator() {
  return <span className="font-display text-xl text-gray-700 leading-none pb-5">:</span>;
}

export default function CountdownTimer({ expiresAt, frozen, lifetime }) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(expiresAt));

  useEffect(() => {
    if (frozen || lifetime) return;
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(expiresAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, frozen, lifetime]);

  if (lifetime) {
    return (
      <div className="inline-flex items-center gap-2 text-brand-200">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        <span className="font-display font-semibold text-lg">Lifetime</span>
      </div>
    );
  }

  if (frozen) {
    return (
      <div className="inline-flex items-center gap-2 text-sky-300">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M12 2v20M5 7l7-5 7 5M5 17l7 5 7-5M2 12h20M5 7l7 5-7 5M19 7l-7 5 7 5" />
        </svg>
        <span className="font-medium">Subscription frozen</span>
      </div>
    );
  }

  if (!timeLeft) {
    return <span className="text-gray-500 font-medium">Expired</span>;
  }

  // Sürgősség szín kódolás: 24 órán belül piros, 3 napon belül sárga, egyébként brand
  const isUrgent = timeLeft.totalMs < 24 * 60 * 60 * 1000;
  const isWarning = !isUrgent && timeLeft.totalMs < 3 * 24 * 60 * 60 * 1000;
  const tone = isUrgent ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-brand-200';

  return (
    <div className="inline-flex items-start gap-3">
      {timeLeft.days > 0 && (
        <>
          <TimeUnit value={timeLeft.days} label="days" tone={tone} />
          <Separator />
        </>
      )}
      <TimeUnit value={timeLeft.hours} label="hours" tone={tone} pad />
      <Separator />
      <TimeUnit value={timeLeft.minutes} label="min" tone={tone} pad />
      {timeLeft.days === 0 && (
        <>
          <Separator />
          <TimeUnit value={timeLeft.seconds} label="sec" tone={tone} pad />
        </>
      )}
    </div>
  );
}
